import { Link, type Href } from 'expo-router';
import Head from 'expo-router/head';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import MapPin from 'lucide-react-native/icons/map-pin';
import { useCallback, useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { listSeedScript } from '@/components/detail/list-seed';
import { BoatCard } from '@/components/riviera/boat-card';
import { HubFilterStatus, HubFilters, boatFilterGroups } from '@/components/riviera/hub-filters';
import { LoadMoreButton } from '@/components/riviera/load-more-button';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Fonts, MaxContentWidth, Spacing } from '@/constants/theme';
import { useBoats } from '@todo/core/hooks/use-boats';
import { useCityBoatFilters } from '@/hooks/use-city-boat-filters';
import { useTheme } from '@/hooks/use-theme';
import {
  BOAT_TYPE_PAGES,
  MIN_BOATS_PER_CITY_PAGE,
  TOP_BOATS_ON_CITY_PAGE,
  boatCityStats,
  boatTypeKey,
  boatTypePageOf,
  type BoatCityStats,
  type BoatTypePages,
} from '@/lib/boat-rental';
import type { Boat } from '@todo/core/boats';
import { cities } from '@/lib/events-data';
import {
  BOAT_TYPES,
  DEFAULT_BOAT_FILTERS,
  isDefaultFilters,
  type BoatFilters,
  type BoatTypeFilter,
} from '@todo/core/hub-filters';
import { boatCityIntro, localizedBoatTitle } from '@/lib/i18n/boat-content';
import { boatRentalCityCopy, boatTypePlural } from '@/lib/i18n/boat-rental-content';
import { cityPageCopy } from '@/lib/i18n/city-content';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import {
  BOAT_RENTAL_CITIES_SEED_KEY,
  BOAT_TYPE_PAGES_SEED_KEY,
  HUB_PAGE_SIZE,
  LIST_BUCKET,
  boatRentalCityQuery,
  boatRentalCitySeedKey,
  boatRentalStatsSeedKey,
} from '@todo/core/list-queries';
import {
  SITE_NAME,
  absoluteUrl,
  boatsCrumb,
  breadcrumbJsonLd,
  faqPageJsonLd,
  hreflangLinks,
  itemListJsonLd,
  richResultImage,
  seoTitle,
  truncate,
} from '@todo/core/seo';
import { getPrefetched } from '@todo/core/static-prefetch';

type Translate = (key: string, options?: Record<string, unknown>) => string;

// Boat prices are all EUR (Click&Boat France); same "€120" format as BoatCard.
export function eur(value: number): string {
  return `€${Math.round(value)}`;
}

export function capitalize(text: string, locale: Locale): string {
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

/**
 * A boat's title without its "Catamaran rental in Cannes: " lead — the generated
 * titles open with type + city, which a type page's list already says in its
 * headings (and which pushed the model name out of the truncated row). Titles
 * with no colon are returned whole.
 */
export function boatListName(title: string): string {
  const colon = title.indexOf(':');
  const rest = colon >= 0 ? title.slice(colon + 1).trim() : '';
  return rest || title;
}

/**
 * The city FAQ, built from the page's own numbers (prices per type, licence-free
 * boats, departure ports) so every city's answers differ — unlike the site-wide
 * boat FAQ, which is identical on all boat pages.
 */
function cityFaq(stats: BoatCityStats, inCity: string, locale: Locale, t: Translate) {
  if (stats.total === 0) return [];
  const sep = t('boatRental.listSeparator');
  const faq: { q: string; a: string }[] = [];

  if (stats.minPrice != null && stats.medianPrice != null) {
    const list = stats.types
      .filter((type) => type.minPrice != null)
      .map((type) =>
        t('boatRental.faq.typePrice', {
          type: boatTypePlural(locale, type.type),
          price: eur(type.minPrice!),
        }),
      )
      .join(sep);
    faq.push({
      q: t('boatRental.faq.priceQ', { inCity }),
      a: t('boatRental.faq.priceA', {
        inCity,
        min: eur(stats.minPrice),
        median: eur(stats.medianPrice),
        list,
      }),
    });
  }

  const licenceFree = stats.types.find((type) => type.type === 'Boat without licence');
  faq.push({
    q: t('boatRental.faq.licenceQ', { inCity }),
    a:
      licenceFree && licenceFree.minPrice != null
        ? t('boatRental.faq.licenceYesA', {
            inCity,
            count: licenceFree.count,
            price: eur(licenceFree.minPrice),
          })
        : t('boatRental.faq.licenceNoA', { inCity }),
  });

  if (stats.ports.length > 0) {
    faq.push({
      q: t('boatRental.faq.portsQ', { inCity }),
      a: t('boatRental.faq.portsA', {
        inCity,
        list: stats.ports.map((port) => `${port.name} (${port.count})`).join(sep),
      }),
    });
  }

  faq.push({
    q: t('boatRental.faq.typesQ', { inCity }),
    a: t('boatRental.faq.typesA', {
      inCity,
      list: stats.types
        .map((type) => `${boatTypePlural(locale, type.type)} (${type.count})`)
        .join(sep),
    }),
  });

  return faq;
}

/**
 * The /[lang]/boat-rental/[city] landing page — one city's whole Click&Boat
 * fleet. Built because Google was crawling the ~2,400 individual boat pages
 * without indexing them: this gives each "boat rental {city}" query a page
 * with real per-city substance (prices and counts by boat type, departure
 * ports, a data-driven FAQ) and gives every boat detail page a crawlable
 * inbound link from its city's page.
 */
export function BoatRentalScreen({ cityId }: { cityId: string }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const city = cities.find((c) => c.id === cityId);
  const copy = city ? boatRentalCityCopy(locale, city.id) : undefined;

  // Always called (rules of hooks). An unknown city id only arrives via a bad
  // client-side navigation — generateBoatRentalCityStaticParams never emits
  // one — and just returns no rows.
  const { boats, loading, error } = useBoats(
    boatRentalCityQuery(cityId),
    city ? boatRentalCitySeedKey(city.id, locale) : undefined,
  );

  // Baked at export from the full rows (the ports come from booking_url, which
  // the card seed drops). Without a seed (native) it's derived from the rows.
  const [bakedStats] = useState(() =>
    city ? getPrefetched<BoatCityStats>(LIST_BUCKET, boatRentalStatsSeedKey(city.id)) : undefined,
  );
  const stats = bakedStats ?? boatCityStats(boats);

  // Which cities have their own page — the exact list the export generated on
  // web; on native, where every route resolves, all of them.
  const [bakedCityIds] = useState(() =>
    getPrefetched<string[]>(LIST_BUCKET, BOAT_RENTAL_CITIES_SEED_KEY),
  );
  const pageCityIds = bakedCityIds ?? cities.map((c) => c.id);

  // This city's /boat-rental/[city]/[type] pages — the exact list the export
  // generated; on native, where nothing is baked, none are linked.
  const [bakedTypePages] = useState(() =>
    getPrefetched<BoatTypePages>(LIST_BUCKET, BOAT_TYPE_PAGES_SEED_KEY),
  );
  const typePages = (city && bakedTypePages?.[city.id]) || [];

  // Filters + sort: after hydration only — see useCityBoatFilters. While any
  // is set, a grid of the matching boats replaces the top cards + link list.
  const [filters, setFilters] = useState<BoatFilters>(DEFAULT_BOAT_FILTERS);
  const filtered = useCityBoatFilters(
    cityId,
    filters,
    isDefaultFilters(filters, DEFAULT_BOAT_FILTERS),
  );
  const [shown, setShown] = useState(HUB_PAGE_SIZE);
  useEffect(() => setShown(HUB_PAGE_SIZE), [filters, cityId]);
  const setFilter = useCallback(
    (id: string, value: string) => setFilters((prev) => ({ ...prev, [id]: value })),
    [],
  );
  const resetFilters = useCallback(() => setFilters(DEFAULT_BOAT_FILTERS), []);

  if (!city || !copy) {
    return (
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Head key={locale}>
          <title>{`${t('common.notFound')} — ${SITE_NAME}`}</title>
          <meta name="robots" content="noindex, follow" />
        </Head>
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <Text style={[styles.status, { color: theme.mutedForeground, paddingHorizontal: Spacing.three }]}>
          {t('common.notFound')}
        </Text>
      </View>
    );
  }

  const inCity = copy.inCity;
  const path = `boat-rental/${city.id}`;
  const canonical = absoluteUrl(locale, path);
  const title = seoTitle(copy.seoTitle);
  const metaDescription = truncate(
    stats.total > 0 && stats.minPrice != null
      ? t('boatRental.metaDescription', { inCity, count: stats.total, price: eur(stats.minPrice) })
      : copy.title,
  );
  const ogImage = richResultImage(boats[0]?.image_url);
  // Too few boats to be worth indexing — see MIN_BOATS_PER_CITY_PAGE. Only
  // once loaded, so a page still fetching client-side isn't flagged.
  const thin = !loading && boats.length < MIN_BOATS_PER_CITY_PAGE;
  const intro = boatCityIntro(city.id, locale);
  const cityGuide = cityPageCopy(city.id, locale);
  const faq = cityFaq(stats, inCity, locale, t);
  const top = boats.slice(0, TOP_BOATS_ON_CITY_PAGE);

  // Only the boat types this city has (from the baked stats, so the static
  // HTML and the first client render agree).
  const cityTypes = new Set(stats.types.map((type) => type.type));
  const filterGroups = boatFilterGroups(filters, t).map((group) =>
    group.id !== 'type'
      ? group
      : {
          ...group,
          options: group.options.filter((option) => {
            const types = BOAT_TYPES[option.value as BoatTypeFilter];
            // `types` is null for "all boats"; a selected option always stays.
            return types == null || option.value === group.value || types.some((type) => cityTypes.has(type));
          }),
        },
  );

  // Every boat, grouped by type in the stats' order (most common first).
  const groups = new Map<string, Boat[]>();
  for (const boat of boats) {
    const key = boatTypeKey(boat);
    const rows = groups.get(key);
    if (rows) rows.push(boat);
    else groups.set(key, [boat]);
  }
  const orderedGroups = [...groups.entries()].sort(
    (a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]),
  );

  const statsLine =
    stats.total > 0 && stats.minPrice != null
      ? [
          t('boatRental.stats', { count: stats.total, price: eur(stats.minPrice) }),
          stats.avgRating != null ? t('boatRental.ratingStat', { rating: stats.avgRating }) : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        {thin ? <meta name="robots" content="noindex, follow" /> : null}
        <link rel="canonical" href={canonical} />
        {hreflangLinks(path).map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [boatsCrumb(locale), { name: copy.title }]))}
        </script>
        {top.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                top.map((boat) => ({
                  name: localizedBoatTitle(boat, locale),
                  url: absoluteUrl(locale, `boats/${boat.id}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        {faq.length > 0 ? (
          <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(faq))}</script>
        ) : null}
        {/* Baked rows + stats so the client's first render matches this static HTML. */}
        {listSeedScript(boatRentalCitySeedKey(city.id, locale))}
        {listSeedScript(boatRentalStatsSeedKey(city.id))}
        {listSeedScript(BOAT_RENTAL_CITIES_SEED_KEY)}
        {listSeedScript(BOAT_TYPE_PAGES_SEED_KEY)}
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView
        style={{ flex: 1 }}>
        <View style={styles.centered}>
          <View style={styles.page}>
            {/* role="heading" + aria-level renders a real <h1> on web (via
                react-native-web) while staying valid on native. */}
            <Text
              role="heading"
              aria-level={1}
              style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
              {copy.title}
            </Text>
            {statsLine ? (
              <Text style={[styles.stats, { color: theme.gold }]}>{statsLine}</Text>
            ) : null}
            {intro ? <Text style={[styles.intro, { color: theme.text }]}>{intro}</Text> : null}

            {pageCityIds.length > 1 ? (
              <View style={styles.citiesBox}>
                <Text style={[styles.smallHeading, { color: theme.mutedForeground }]}>
                  {t('boatRental.citiesHeading')}
                </Text>
                <View style={styles.chipsRow}>
                  {pageCityIds.map((id) => (
                    <CityChip
                      key={id}
                      label={cities.find((c) => c.id === id)?.name ?? id}
                      active={id === city.id}
                      href={{ pathname: '/[lang]/boat-rental/[city]', params: { lang: locale, city: id } }}
                    />
                  ))}
                </View>
              </View>
            ) : null}

            {typePages.length > 0 ? (
              <View style={styles.citiesBox}>
                <Text style={[styles.smallHeading, { color: theme.mutedForeground }]}>
                  {t('boatRental.typeChipsHeading', { inCity })}
                </Text>
                <View style={styles.chipsRow}>
                  {typePages.map((type) => (
                    <CityChip
                      key={type}
                      pin={false}
                      active={false}
                      label={t(`hubFilters.boatTypeOptions.${BOAT_TYPE_PAGES[type]}`)}
                      href={{
                        pathname: '/[lang]/boat-rental/[city]/[type]',
                        params: { lang: locale, city: city.id, type },
                      }}
                    />
                  ))}
                </View>
              </View>
            ) : null}

            {loading ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('boatRental.loading')}
              </Text>
            ) : error ? (
              <Text style={[styles.status, { color: theme.destructive }]}>{error}</Text>
            ) : boats.length === 0 ? (
              <Text style={[styles.status, { color: theme.mutedForeground }]}>
                {t('boatRental.empty')}
              </Text>
            ) : (
              <>
                {/* Right under the city chips, with its results right under
                    it; the types/ports summary stays below either way. */}
                <View style={styles.section}>
                  <HubFilters groups={filterGroups} onChange={setFilter} onReset={resetFilters} />
                  <HubFilterStatus
                    filtered={filtered.rows != null}
                    total={filtered.rows?.length ?? null}
                    loading={filtered.loading}
                    error={filtered.error}
                    countLabel={(count) => t('hubFilters.boatsCount', { count })}
                    onRetry={filtered.retry}
                    onReset={resetFilters}
                  />
                </View>

                {filtered.rows ? (
                  <View style={styles.section}>
                    <View style={styles.grid}>
                      {filtered.rows.slice(0, shown).map((boat, index) => (
                        <BoatCard key={boat.id} boat={boat} eager={index < 4} />
                      ))}
                    </View>
                    {filtered.rows.length > shown ? (
                      <LoadMoreButton
                        onPress={() => setShown((n) => n + HUB_PAGE_SIZE)}
                        loading={false}
                        error={false}
                      />
                    ) : null}
                  </View>
                ) : null}

                {stats.types.length > 0 ? (
                  <View style={styles.section}>
                    <SectionHeading>{t('boatRental.typesHeading')}</SectionHeading>
                    {stats.types.map((type) => {
                      const typePage = boatTypePageOf(type.type);
                      const name = capitalize(boatTypePlural(locale, type.type), locale);
                      return (
                        <View
                          key={type.type}
                          style={[styles.typeRow, { borderBottomColor: theme.border }]}>
                          {typePage && typePages.includes(typePage) ? (
                            <Link
                              href={{
                                pathname: '/[lang]/boat-rental/[city]/[type]',
                                params: { lang: locale, city: city.id, type: typePage },
                              }}
                              style={[styles.typeName, { color: theme.primary }]}>
                              {name}
                            </Link>
                          ) : (
                            <Text style={[styles.typeName, { color: theme.text }]}>{name}</Text>
                          )}
                          <Text style={[styles.typeFacts, { color: theme.textSecondary }]}>
                            {t('boatRental.typeRow', {
                              count: type.count,
                              price: type.minPrice != null ? eur(type.minPrice) : '—',
                              median: type.medianPrice != null ? eur(type.medianPrice) : '—',
                            })}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                ) : null}

                {stats.ports.length > 0 ? (
                  <View style={styles.section}>
                    <SectionHeading>{t('boatRental.portsHeading')}</SectionHeading>
                    <View style={styles.chipsRow}>
                      {stats.ports.map((port) => (
                        <View
                          key={port.name}
                          style={[styles.chip, { backgroundColor: theme.card, borderColor: theme.border }]}>
                          <MapPin size={12} color={theme.mutedForeground} />
                          <Text style={[styles.chipText, { color: theme.text }]}>
                            {`${port.name} · ${port.count}`}
                          </Text>
                        </View>
                      ))}
                    </View>
                  </View>
                ) : null}

                {filtered.rows ? null : (
                  <>
                    {/* The static HTML: every boat in the city stays linked
                        from here (BoatGroup), whatever the filters do later. */}
                    <View
                      aria-busy={filtered.loading}
                      style={[styles.section, filtered.loading && styles.stale]}>
                      <SectionHeading>{t('boatRental.topHeading', { inCity })}</SectionHeading>
                      <View style={styles.grid}>
                        {/* First row is above the fold on desktop — eager, as on /boats. */}
                        {top.map((boat, index) => (
                          <BoatCard key={boat.id} boat={boat} eager={index < 4} />
                        ))}
                      </View>
                    </View>

                    <View style={[styles.section, filtered.loading && styles.stale]}>
                      <SectionHeading>{t('boatRental.allHeading', { inCity })}</SectionHeading>
                      {orderedGroups.map(([type, rows]) => (
                        <BoatGroup key={type} type={type} rows={rows} locale={locale} />
                      ))}
                    </View>
                  </>
                )}
              </>
            )}

            {faq.length > 0 ? (
              <View style={styles.section}>
                <SectionHeading>{t('boatRental.faqHeading', { inCity })}</SectionHeading>
                {faq.map((entry) => (
                  <View key={entry.q} style={styles.faqItem}>
                    <Text style={[styles.faqQuestion, { color: theme.text }]}>{entry.q}</Text>
                    <Text style={[styles.faqAnswer, { color: theme.textSecondary }]}>{entry.a}</Text>
                  </View>
                ))}
              </View>
            ) : null}

            <View style={styles.moreLinks}>
              {cityGuide ? (
                <MoreLink
                  label={cityGuide.title}
                  href={{ pathname: '/[lang]/[city]', params: { lang: locale, city: city.id } }}
                />
              ) : null}
              <MoreLink
                label={t('boatRental.allBoats')}
                href={{ pathname: '/[lang]/boats', params: { lang: locale } }}
              />
            </View>
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

export function SectionHeading({ children }: { children: string }) {
  const theme = useTheme();
  return (
    <Text
      role="heading"
      aria-level={2}
      style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
      {children}
    </Text>
  );
}

/** One compact row of the "all boats" list — a plain text link, far lighter than a card. */
/** Boats listed per type before the "Show all" toggle — three rows of the desktop grid. */
const VISIBLE_BOATS_PER_TYPE = 9;
/** Don't collapse a group to hide just a couple of boats behind a toggle. */
const MIN_HIDDEN_BOATS = 4;

/**
 * One boat type in the "all boats" list, collapsed to its first few boats.
 *
 * The collapsed boats are hidden with `display: none`, not left unrendered:
 * this list is the only place most boat detail pages are linked from, so every
 * link must stay in the static HTML — Google follows links in collapsed
 * sections. Only the visitor sees the shorter list.
 */
export function BoatGroup({
  type,
  rows,
  locale,
  shortNames = false,
}: {
  type: string;
  rows: Boat[];
  locale: Locale;
  /** Drop each title's type + city lead (boatListName) — for a page that is already one type in one city. */
  shortNames?: boolean;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const collapsible = rows.length >= VISIBLE_BOATS_PER_TYPE + MIN_HIDDEN_BOATS;
  const typeLabel = boatTypePlural(locale, type);

  return (
    <View style={styles.group}>
      <Text role="heading" aria-level={3} style={[styles.groupHeading, { color: theme.text }]}>
        {`${capitalize(typeLabel, locale)} (${rows.length})`}
      </Text>
      <View style={styles.boatList}>
        {rows.map((boat, index) => (
          <BoatLink
            key={boat.id}
            boat={boat}
            locale={locale}
            shortName={shortNames}
            hidden={collapsible && !open && index >= VISIBLE_BOATS_PER_TYPE}
          />
        ))}
      </View>
      {collapsible ? (
        <Pressable
          accessibilityRole="button"
          aria-expanded={open}
          onPress={() => setOpen((o) => !o)}
          style={styles.showAll}>
          {({ pressed }) => (
            <Text style={[styles.showAllText, { color: theme.gold }, pressed && { opacity: 0.6 }]}>
              {open
                ? t('boatRental.showFewer')
                : t('boatRental.showAllType', { count: rows.length, type: typeLabel })}
            </Text>
          )}
        </Pressable>
      ) : null}
    </View>
  );
}

function BoatLink({
  boat,
  locale,
  shortName,
  hidden,
}: {
  boat: Boat;
  locale: Locale;
  shortName: boolean;
  hidden: boolean;
}) {
  const theme = useTheme();
  const title = localizedBoatTitle(boat, locale);
  return (
    <Link
      href={{ pathname: '/[lang]/boats/[id]', params: { lang: locale, id: boat.id } }}
      asChild>
      {/* Flattened: <Link asChild> rejects a style array on its child. */}
      <Pressable
        accessibilityRole="link"
        style={StyleSheet.flatten([styles.boatLink, hidden && styles.hidden])}>
        {({ pressed }) => (
          <Text numberOfLines={1} style={[styles.boatLinkText, pressed && { opacity: 0.6 }]}>
            <Text style={{ color: theme.primary }}>{shortName ? boatListName(title) : title}</Text>
            {boat.from_price != null ? (
              <Text style={{ color: theme.mutedForeground }}>{` · ${eur(boat.from_price)}`}</Text>
            ) : null}
          </Text>
        )}
      </Pressable>
    </Link>
  );
}

export function CityChip({
  label,
  active,
  href,
  pin = true,
}: {
  label: string;
  active: boolean;
  href: Href;
  /** The map-pin icon — off for chips that aren't places (boat types). */
  pin?: boolean;
}) {
  const theme = useTheme();
  return (
    // Pill styling on an inner View — see the same note in wine-tours-screen.tsx.
    <Link href={href} asChild>
      <Pressable accessibilityRole="link">
        {({ pressed }) => (
          <View
            style={[
              styles.chip,
              {
                backgroundColor: active ? theme.accent : theme.card,
                borderColor: active ? theme.accent : theme.border,
              },
              pressed && { opacity: 0.85 },
            ]}>
            {pin ? (
              <MapPin size={12} color={active ? theme.accentForeground : theme.mutedForeground} />
            ) : null}
            <Text style={[styles.chipText, { color: active ? theme.accentForeground : theme.text }]}>
              {label}
            </Text>
          </View>
        )}
      </Pressable>
    </Link>
  );
}

export function MoreLink({ label, href }: { label: string; href: Href }) {
  const theme = useTheme();
  return (
    <Link href={href} asChild>
      <Pressable accessibilityRole="link" style={styles.moreLink}>
        {({ pressed }) => (
          <>
            <Text style={[styles.moreLinkText, { color: theme.primary }, pressed && { opacity: 0.6 }]}>
              {label}
            </Text>
            <ArrowRight size={16} color={theme.primary} style={pressed && { opacity: 0.6 }} />
          </>
        )}
      </Pressable>
    </Link>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    gap: Spacing.three,
  },
  heading: {
    fontSize: 26,
    fontWeight: '700',
  },
  stats: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: -Spacing.two,
  },
  intro: {
    fontSize: 15,
    lineHeight: 23,
  },
  citiesBox: {
    gap: Spacing.two,
  },
  smallHeading: {
    fontSize: 13,
    fontWeight: '600',
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.one + 2,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: Spacing.two,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 999,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '500',
  },
  section: {
    gap: Spacing.two,
    marginTop: Spacing.two,
  },
  sectionHeading: {
    fontSize: 22,
    fontWeight: '700',
  },
  typeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: Spacing.two,
    paddingVertical: Spacing.two,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  typeName: {
    fontSize: 15,
    fontWeight: '600',
  },
  typeFacts: {
    fontSize: 14,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.three,
    justifyContent: 'center',
  },
  group: {
    gap: Spacing.one,
    marginTop: Spacing.two,
  },
  groupHeading: {
    fontSize: 16,
    fontWeight: '700',
  },
  boatList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: Spacing.four,
  },
  boatLink: {
    flexBasis: 320,
    flexGrow: 1,
    paddingVertical: 4,
  },
  boatLinkText: {
    fontSize: 14,
  },
  hidden: {
    display: 'none',
  },
  showAll: {
    alignSelf: 'flex-start',
    paddingVertical: Spacing.two,
  },
  showAllText: {
    fontSize: 14,
    fontWeight: '600',
  },
  faqItem: {
    gap: 4,
  },
  faqQuestion: {
    fontSize: 14,
    fontWeight: '700',
  },
  faqAnswer: {
    fontSize: 14,
    lineHeight: 21,
  },
  moreLinks: {
    marginTop: Spacing.two,
    gap: Spacing.two,
  },
  moreLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  moreLinkText: {
    fontSize: 14,
    fontWeight: '600',
  },
  status: {
    fontSize: 14,
    paddingVertical: Spacing.four,
  },
  stale: {
    opacity: 0.5,
  },
});
